import type { SSRContext } from '@seamless/oneweb-ssr';
import type { VehicleJsonLdData } from '@dh-io-owpi/shared-dynamic-stage/src/service/jsonLd';
import { fetchTranslations } from '@dh-io-owpi/shared/src/plugins/i18n/api';
import { createErrorManager, type ErrorManager } from '@dh-io-owpi/shared/src/components/ErrorList/manager';
import { sendApiError } from '@dh-io-owpi/shared/src/services/Error';
import { convertToSlug } from '@dh-io-owpi/shared/src/utils/path';
import { logger } from '@dh-io-owpi/shared/src/services/logger';
import { logCtxFromAemData } from '@dh-io-owpi/shared/src/services/logger/ctx';
import type { AemData, ComponentData } from '@type/aem';
import { getVehicleBaseData, getVehicleInitialData } from '@dh-io-owpi/backend-api/src/VehicleData';
import type {
  Paint,
  VehicleBaseData,
  BrandId,
  FeatureCategory,
  ApiTechnicalDataSheet,
} from '@dh-io-owpi/backend-api/src/data-contracts';
import type { BaseLoaderData } from '@dh-io-owpi/shared/src/ssr/types';
import type { JsonLdLoaderData, JsonLdLoaderDataOutput } from '@dh-io-owpi/shared/src/ssr/jsonLdHtmlRender';
import type { VehicleStyle } from '@/lib/api';
import { notFoundError } from './utils/loaderErrors';
import { getPageUrls } from './utils/pageUrls';
import { setStyleToUrl, getStyleSlugFromUrl } from './stores/pageState/utils';
import { loadVehicleFeatures } from './stores/features';
import type { SectionVisibility, SectionName } from './stores/sections';
import { getVisibleSections } from './stores/sections/utils';
import { addUspsToStyle, extractHighlightsFromAemDataToStyles } from './sections/usp/utils/SpaHighlight';
import { fetchConfiguration, type ConfigBase } from './composables/useSpaConfig';

// Used to filter the translations in Phrase, downloading only a subset of the translations necessary for the component.
export const componentPhraseTag = 'spa';

export interface LoaderData extends BaseLoaderData<AemData>, InitialStageData {
  hiddenSections?: SectionName[];
  config: ConfigBase;
}

export type RenderData = JsonLdLoaderDataOutput<LoaderData>;

/**
 * isomorphic function to load data for the dynamic stage
 * this function is used in the SSR and in the CSR (fallback mode)
 */
export async function loadData(aemData: AemData, context: SSRContext<ComponentData>): Promise<LoaderData> {
  const errorManager = createErrorManager(aemData);

  const config = await fetchConfiguration(aemData);

  const [[initialStageData, visibleSections], messages] = await Promise.all([
    loadInitialStageData(aemData, config, errorManager),
    fetchTranslations(aemData, componentPhraseTag),
  ]);

  // Add a timestamp to the data to be able to check if the data is fresh or not
  const fetchedAt = new Date().toJSON();

  /// hide sections that are not available
  const hiddenSections: SectionName[] = [];

  if (!initialStageData.vehicleFeatures.length && visibleSections.features) {
    // If there are no features, hide the features section
    hiddenSections.push('features');
  }

  if (!initialStageData.technicalDataSheet && visibleSections.techData) {
    // If there is no technical data sheet, hide the techData section
    hiddenSections.push('techData');
  }

  return {
    ...initialStageData,
    config,
    hiddenSections,
    fetchedAt,
    aemData,
    messages,
    // we don't need errors to be shown in publish. Remove this check for authorMode if we want to keep track of this errors even in publish
    errors: aemData.isAemEditMode ? errorManager.errors : [],
    wbVersion: context.globalAEMContext['second-workbench-version'],
  };
}

export interface InitialStageData extends JsonLdLoaderData<VehicleJsonLdData> {
  modelSeries: string;
  modelName: string;
  bm4: string;
  bodyTypeId: string;
  styles: VehicleStyle[];
  paints: Paint[];
  currentStyleId: string;
  currentPaintId: string;
  vehicleBaseData: VehicleBaseData;
  vehicleFeatures: FeatureCategory[];
  technicalDataSheet?: ApiTechnicalDataSheet | null;
  availableBrands: BrandId[];
  footnotes: Record<string, string>;
}

async function loadInitialStageData(
  aemData: AemData,
  config: ConfigBase,
  errorManager: ErrorManager,
): Promise<[initialStageData: InitialStageData, visibleSections: SectionVisibility]> {
  const {
    styles: allStyles,
    footnotes,
    vehicle,
    context,
    seoData,
    technicalDataSheet,
  } = await loadVehicleInitialData(aemData, errorManager);
  let vehicleBaseData: VehicleBaseData = { vehicle, context };

  if (!context.availableBrands.includes(aemData.subBrand)) {
    throw notFoundError(
      `The sub-brand '${aemData.subBrand}' is not available for the model series: '${aemData.modelSeries}'`,
    );
  }

  const stylesForBrand = allStyles.filter((style) => style.brand === aemData.subBrand);

  const [uspsForStyle, uspValidationErrors] = extractHighlightsFromAemDataToStyles(aemData.highlights, stylesForBrand);

  errorManager.addNotifications(uspValidationErrors);

  const pageUrls = await getPageUrls(aemData, context.availableBrands as BrandId[]);
  const relForStylePerSubBrand: Partial<Record<BrandId, true>> = {};
  const styles = allStyles.flatMap<VehicleStyle>((style) => {
    const brandId = style.brand as BrandId;
    let url = pageUrls[brandId];
    let rel: string | undefined;

    const vehicleStyle: VehicleStyle = addUspsToStyle(style, uspsForStyle[style.name] ?? []);

    // If the style is of the same sub-brand as the current page, we don't need anchor buttons to switch to it.
    if (style.brand === aemData.subBrand) return [vehicleStyle];

    // If we have no url for the sub-brand, filter out the style.
    if (!url) return [];

    const notFirstStyle = relForStylePerSubBrand[brandId];
    relForStylePerSubBrand[brandId] = true;

    if (!aemData._demoMode || import.meta.env.SSR) {
      if (notFirstStyle) {
        // If this is not the first style of the brand, we add the `rel="nofollow"` attribute to the anchor, because we don't want crawlers to follow this link.
        rel = 'nofollow';
        // If the style is not the first of the brand, we add something to the URL to differentiate it from the first style.
        url = setStyleToUrl(url, style.name);
      } else {
        // If this is the first style of the brand, we don't add anything to the URL, but we may clean it up.
        url = setStyleToUrl(url);
      }
    }

    return [{ ...vehicleStyle, anchor: { url, rel } }];
  });

  if (!styles.length) {
    throw notFoundError('No styles found for the model series: ' + aemData.modelSeries);
  }

  const styleSlug = getStyleSlugFromUrl(aemData.url);

  let styleFromSlug: VehicleStyle | undefined;
  if (styleSlug) {
    styleFromSlug = styles.find((s) => s.brand === aemData.subBrand && convertToSlug(s.name) === styleSlug);

    if (!styleFromSlug) {
      // something was passed in the URL, but we couldn't find the style
      logger.warn(
        `[USER ERROR][${aemData.country}][${aemData.modelSeries}] No style found for the style slug: ${styleSlug}`,
        { ...logCtxFromAemData(aemData), errorType: 'USER', styleSlug },
      );
    } else {
      /// It is a bit shitty to call the API again to get the vehicleBaseData for the specified style in the url.
      /// I'm not seeing an easy way to avoid this, unless we use id's in the URL and change the getVehicleInitialData to accept it.
      /// but that would be a bit ugly.
      const paint = styleFromSlug.paints.find((p) => p.id == context.selectedPaintId) ?? styleFromSlug.paints[0];
      vehicleBaseData = await getVehicleBaseData(aemData.modelSeries, encodeURIComponent(paint.vehicleId)) //
        .then(({ data }) => data, errorManager.sendError);
    }
  }

  const style = styleFromSlug ?? styles.find((s) => s.id === context.selectedStyleId);
  if (!style) {
    throw notFoundError(
      'No style found for the sub-brand: ' + aemData.subBrand + ' in the model series: ' + aemData.modelSeries,
    );
  }

  const paints = style.paints;
  if (!paints.length) {
    throw notFoundError(
      'No paints found for the style: ' + style.name + ' in the model series: ' + aemData.modelSeries,
    );
  }

  const visibleSections = getVisibleSections(aemData._sections);

  let vehicleFeatures: FeatureCategory[] = [];

  if (visibleSections.features) {
    const paint = paints.find((p) => p.id == context.selectedPaintId) ?? paints[0];
    vehicleFeatures = await loadVehicleFeatures(aemData, paint.vehicleId) //
      .then(
        ({ data }) => data.categories,
        (e) => {
          sendApiError(aemData, e);
          return [];
        },
      );
  }

  // Remove price information from JSON-LD when we're using PLS
  if (config.stage.price.source === 'pls' && seoData.offers) {
    // @ts-ignore
    delete seoData.offers.price;
    // @ts-ignore
    delete seoData.offers.priceCurrency;
  }

  return [
    {
      modelSeries: aemData.modelSeries,
      modelName: vehicle.modelName,
      bm4: vehicle.bm4,
      bodyTypeId: vehicle.bodyTypeId,
      footnotes,
      styles,
      paints,
      currentStyleId: style.id,
      currentPaintId: context.selectedPaintId,
      vehicleBaseData,
      vehicleFeatures,
      jsonLd: seoData,
      technicalDataSheet,
      availableBrands: context.availableBrands as BrandId[],
    },
    visibleSections,
  ];
}

function loadVehicleInitialData(aemData: AemData, errorManager?: ErrorManager) {
  return getVehicleInitialData({
    marketId: `${aemData.language}_${aemData.country}`,
    modelSeries: aemData.modelSeries,
    'brand-id': aemData.subBrand,
  }).then(({ data }) => {
    errorManager?.addValidations(data.errors);
    return data;
  }, errorManager?.sendError);
}
